import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { Button, Form, OverlayTrigger, Tooltip as BootstrapTooltip } from "react-bootstrap";
import AnomalyPeriodSelector from "./AnomalyPeriodSelector";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { ScaleLoader } from "react-spinners";
import { FcFinePrint } from "react-icons/fc";
import {
  FiActivity,
  FiBarChart2,
  FiCheckCircle,
  FiGitBranch,
  FiGrid,
  FiPieChart,
  FiTrendingUp,
} from "react-icons/fi";
import { BsFiletypePdf } from "react-icons/bs";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import moment from "moment";
import { RegulationContext } from "../../../App";
import AnomalyService from "../../../core/service/AnomalyService";
import { resolveAnomalyRuleLabel } from "../../../core/constants/anomalyRuleCodes";
import {
  ANOMALY_SEVERITY_COLORS,
  ANOMALY_SEVERITY_ORDER,
  formatAnomalyStatusLabel,
} from "../../../core/constants/anomalySeverity";
import Utils from "../../../core/utilities/Utils";
import AnomalyInfoTip from "./AnomalyInfoTip";
import { useAnomalyDetection } from "./AnomalyDetectionContext";
import {
  formatAnomalyApiDate,
  formatAnomalyDisplayDate,
  formatAnomalyDisplayDateTime,
  formatAnomalyHeatmapPeriod,
  getAnomalyDefaultPeriodFrom,
  getAnomalyDefaultPeriodTo,
  resolveAnomalyPeriodState,
} from "./anomalyDateUtils";
import { buildDashboardTimeSeriesNavState } from "./anomalyNavUtils";
import { resolveAnomalyRegulationId } from "./anomalyRegulationUtils";
import "./AnomalyDashboard.scss";

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip
);

const BRANCH_CHART_LIMIT = 10;

const buildFilters = (regulationId, status, periodFrom, periodTo) => {
  const params = { regulationId };
  if (status) params.status = status;
  if (periodFrom) params.periodFrom = formatAnomalyApiDate(periodFrom);
  if (periodTo) params.periodTo = formatAnomalyApiDate(periodTo);
  return params;
};

const getHeatmapIntensity = (count, maxCount) => {
  if (!count || count === 0 || !maxCount) return 0;
  return count / maxCount;
};

const getHeatmapCellTextColor = (intensity) => {
  if (intensity === 0) return "#94a3b8";
  if (intensity > 0.55) return "#ffffff";
  return "#7f1d1d";
};


const computeResolutionRate = (byStatus) => {
  if (!byStatus) {
    return {
      resolved: 0,
      total: 0,
      rate: 0,
      barColor: ANOMALY_SEVERITY_COLORS.CRITICAL,
    };
  }
  const open = byStatus.OPEN || 0;
  const dismissed = byStatus.DISMISSED || 0;
  const resolved = byStatus.RESOLVED || 0;
  const total = open + dismissed + resolved;
  const rate = total > 0 ? Math.round((resolved / total) * 100) : 0;
  let barColor = ANOMALY_SEVERITY_COLORS.CRITICAL;
  if (rate >= 50) barColor = ANOMALY_SEVERITY_COLORS.MEDIUM;
  else if (rate >= 20) barColor = ANOMALY_SEVERITY_COLORS.HIGH;
  return { resolved, total, rate, barColor };
};

const computeDismissalRate = (byStatus) => {
  if (!byStatus) return { rate: 0, total: 0 };
  const open = byStatus.OPEN || 0;
  const dismissed = byStatus.DISMISSED || 0;
  const resolved = byStatus.RESOLVED || 0;
  const total = open + dismissed + resolved;
  const rate = total > 0 ? dismissed / total : 0;
  return { rate, total };
};

const computeTopRule = (byRule) => {
  if (!byRule || !Object.keys(byRule).length) return null;
  let topCode = null;
  let topCount = 0;
  Object.entries(byRule).forEach(([code, count]) => {
    if (count > topCount) { topCount = count; topCode = code; }
  });
  const total = Object.values(byRule).reduce((s, v) => s + v, 0);
  const pct = total > 0 ? Math.round((topCount / total) * 100) : 0;
  return { code: topCode, count: topCount, pct, total };
};

const getSeverityTotal = (bySeverity) => {
  if (!bySeverity) return 0;
  return Object.values(bySeverity).reduce((s, v) => s + (v || 0), 0);
};

const formatSpectrumCount = (value) => Number(value ?? 0).toLocaleString();

const isStatsEmpty = (bySeverity) => {
  if (!bySeverity || !Object.keys(bySeverity).length) return true;
  return Object.values(bySeverity).every((v) => !v || v === 0);
};

const DASH_STORAGE_KEY = "anomaly_dashboard_filters";

const readDashStorage = () => {
  try { return JSON.parse(sessionStorage.getItem(DASH_STORAGE_KEY) || "{}"); }
  catch { return {}; }
};

const isSameAnomalyDay = (a, b) =>
  (!a && !b) || Boolean(a && b && moment(a).isSame(b, "day"));

const chartCursorHover = (event, elements) => {
  const target = event?.native?.target;
  if (target) target.style.cursor = elements.length ? "pointer" : "default";
};

const AnomalyDashboard = ({ t }) => {
  const { glbRegulation, glbRegulations } = useContext(RegulationContext);
  const { lastCompletedAt, lastRun } = useAnomalyDetection();
  const location = useLocation();
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const navState = location.state || {};

  const apiRegulationId = resolveAnomalyRegulationId(glbRegulation);

  const saved = Object.keys(navState).length > 0 ? {} : readDashStorage();

  const initialStatus = navState.status || saved.status || "";
  const [regulationId, setRegulationId] = useState(navState.regulationId || "");
  const [status, setStatus] = useState(initialStatus);
  const initialPeriods = resolveAnomalyPeriodState(
    navState.periodFrom || saved.periodFrom,
    navState.periodTo || saved.periodTo,
    getAnomalyDefaultPeriodFrom(),
    getAnomalyDefaultPeriodTo()
  );
  const [periodFrom, setPeriodFrom] = useState(initialPeriods.periodFrom);
  const [periodTo, setPeriodTo] = useState(initialPeriods.periodTo);
  const [appliedFilters, setAppliedFilters] = useState({
    status: initialStatus,
    periodFrom: initialPeriods.periodFrom,
    periodTo: initialPeriods.periodTo,
  });


  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [branchDrillParent, setBranchDrillParent] = useState(null);
  const [branchDrillData, setBranchDrillData] = useState([]);
  const [loadingBranchDrill, setLoadingBranchDrill] = useState(false);

  const resolution = useMemo(() => computeResolutionRate(stats?.byStatus), [stats]);
  const dismissal = useMemo(() => computeDismissalRate(stats?.byStatus), [stats]);
  const topRule = useMemo(() => computeTopRule(stats?.byRule), [stats]);
  const severityTotal = useMemo(() => getSeverityTotal(stats?.bySeverity), [stats]);
  const statsEmpty = useMemo(() => isStatsEmpty(stats?.bySeverity), [stats]);

  const filtersDirty = useMemo(() => (
    status !== appliedFilters.status
    || !isSameAnomalyDay(periodFrom, appliedFilters.periodFrom)
    || !isSameAnomalyDay(periodTo, appliedFilters.periodTo)
  ), [status, periodFrom, periodTo, appliedFilters]);

  const hasActiveFilters = useMemo(() => (
    Boolean(appliedFilters.status)
    || !isSameAnomalyDay(appliedFilters.periodFrom, getAnomalyDefaultPeriodFrom())
    || !isSameAnomalyDay(appliedFilters.periodTo, getAnomalyDefaultPeriodTo())
  ), [appliedFilters]);

  const heatmapHotspot = useMemo(() => {
    if (!stats?.heatmap?.length) return null;
    return stats.heatmap.reduce(
      (max, h) => (!max || h.count > max.count ? h : max),
      null
    );
  }, [stats]);

  const heatmapMaxCount = useMemo(() => {
    if (!stats?.heatmap?.length) return 1;
    return Math.max(...stats.heatmap.map((h) => h.count), 1);
  }, [stats]);

  const trendPeak = useMemo(() => {
    if (!stats?.byPeriod || !Object.keys(stats.byPeriod).length) return null;
    return Object.entries(stats.byPeriod).reduce(
      (max, [period, count]) => (!max || count > max.count ? { period, count } : max),
      null
    );
  }, [stats]);

  const trendPeriodLabels = useMemo(() => {
    if (!stats?.byPeriod) return [];
    return Object.keys(stats.byPeriod).sort((a, b) => a.localeCompare(b));
  }, [stats]);

  const sortedBranches = useMemo(() => {
    const source = branchDrillParent ? branchDrillData : (stats?.byBranch || []);
    return [...source].sort((a, b) => b.count - a.count);
  }, [stats, branchDrillParent, branchDrillData]);

  const chartBranches = useMemo(
    () => sortedBranches.slice(0, BRANCH_CHART_LIMIT),
    [sortedBranches]
  );

  const hasMoreBranches = sortedBranches.length > BRANCH_CHART_LIMIT;

  const topBranch = sortedBranches.length > 0 ? sortedBranches[0] : null;

  const regulationKeys = useMemo(
    () => (stats?.byRegulation ? Object.keys(stats.byRegulation) : []),
    [stats]
  );

  const fetchStats = (filters) => {
    setLoadingStats(true);
    AnomalyService.getStats(filters)
      .then((res) => setStats(res.data))
      .catch(() => { })
      .finally(() => setLoadingStats(false));
  };

  const getDateParams = () => {
    const p = {};
    if (periodFrom) p.periodFrom = moment(periodFrom).format("YYYY-MM-DD");
    if (periodTo) p.periodTo = moment(periodTo).format("YYYY-MM-DD");
    return p;
  };

  const openBranchDrilldown = (branchId, branchName) => {
    setBranchDrillParent({ branchId, branchName });
    setBranchDrillData([]);
    setLoadingBranchDrill(true);
    AnomalyService.getBranchDrilldown(branchId, getDateParams())
      .then((res) => setBranchDrillData(res.data || []))
      .catch(() => setBranchDrillData([]))
      .finally(() => setLoadingBranchDrill(false));
  };

  const closeBranchDrilldown = () => {
    setBranchDrillParent(null);
    setBranchDrillData([]);
  };

  const handleExportPdf = () => {
    if (!contentRef.current) return;
    setIsExporting(true);

    // Defer heavy work so React can flush the loading spinner to DOM first,
    // freeing the click handler thread immediately.
    setTimeout(async () => {
    const el = contentRef.current;
    const caption = el.querySelector(".pdf-export-caption");

    // Measure full content size before capture
    const fullWidth = Math.max(el.scrollWidth, el.offsetWidth);
    const fullHeight = Math.max(el.scrollHeight, el.offsetHeight);

    try {
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        // Compensate for current page scroll so the element is captured in full
        scrollX: -window.scrollX,
        scrollY: -window.scrollY,
        // Capture the element's full content dimensions
        width: fullWidth,
        height: fullHeight,
        // Use full document dimensions as the render viewport so nothing
        // on the right or bottom gets clipped by a smaller window size
        windowWidth: Math.max(document.documentElement.scrollWidth, fullWidth),
        windowHeight: Math.max(document.documentElement.scrollHeight, fullHeight),
        onclone: (clonedDoc) => {
          const cap = clonedDoc.querySelector(".pdf-export-caption");
          if (cap) cap.style.display = "block";

          const colorFnRegex = /\bcolor\([^)]+\)/g;

          // Lift overflow:hidden from the cloned document root and the content
          // container ancestors so html2canvas sees the full un-clipped layout.
          clonedDoc.documentElement.style.overflow = "visible";
          clonedDoc.body.style.overflow = "visible";
          if (cap) {
            let ancestor = cap.parentElement;
            while (ancestor && ancestor !== clonedDoc.body) {
              const ov = clonedDoc.defaultView.getComputedStyle(ancestor).overflow;
              if (ov === "hidden") ancestor.style.overflow = "visible";
              ancestor = ancestor.parentElement;
            }
          }

          // Sanitize raw <style> tag text content
          clonedDoc.querySelectorAll("style").forEach((styleEl) => {
            if (styleEl.textContent.includes("color(")) {
              styleEl.textContent = styleEl.textContent.replace(colorFnRegex, "transparent");
            }
          });

          // Sanitize inline style attributes
          clonedDoc.querySelectorAll("[style]").forEach((el) => {
            const s = el.getAttribute("style") || "";
            if (s.includes("color(")) {
              el.setAttribute("style", s.replace(colorFnRegex, "transparent"));
            }
          });

          // Fix computed styles — Chrome on P3/wide-gamut displays returns
          // color(srgb ...) from getComputedStyle(), which html2canvas can't parse.
          // Setting an inline style forces getComputedStyle() to return a safe value.
          const colorProps = [
            "color", "background-color",
            "border-top-color", "border-right-color", "border-bottom-color", "border-left-color",
            "outline-color", "text-decoration-color", "caret-color",
            "fill", "stroke", "stop-color",
          ];
          const clonedWindow = clonedDoc.defaultView;
          clonedDoc.querySelectorAll("*").forEach((el) => {
            try {
              const computed = clonedWindow.getComputedStyle(el);
              colorProps.forEach((prop) => {
                const val = computed.getPropertyValue(prop);
                if (val && val.includes("color(")) {
                  el.style.setProperty(prop, "transparent");
                }
              });
            } catch (_) {
              // skip non-styleable elements (e.g. <svg> defs)
            }
          });
        },
      });

      if (caption) caption.style.display = "none";

      const imgData = canvas.toDataURL("image/jpeg", 0.98);
      const pdf = new jsPDF({
        unit: "mm",
        format: "a4",
        orientation: "landscape",
        compress: true,
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidthMm = pageWidth;
      const imgHeightMm = (canvas.height / canvas.width) * imgWidthMm;

      let remainingHeight = imgHeightMm;
      let yOffset = 0;

      while (remainingHeight > 0) {
        const sliceHeightPx = Math.min(
          (pageHeight / imgHeightMm) * canvas.height,
          canvas.height - yOffset * (canvas.height / imgHeightMm)
        );
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = Math.ceil(sliceHeightPx);

        const ctx = sliceCanvas.getContext("2d");
        ctx.drawImage(
          canvas,
          0,
          yOffset * (canvas.height / imgHeightMm),
          canvas.width,
          Math.ceil(sliceHeightPx),
          0,
          0,
          canvas.width,
          Math.ceil(sliceHeightPx)
        );

        const sliceData = sliceCanvas.toDataURL("image/jpeg", 0.98);
        const sliceDisplayHeight = (sliceCanvas.height / canvas.width) * imgWidthMm;
        pdf.addImage(sliceData, "JPEG", 0, 0, imgWidthMm, sliceDisplayHeight);

        remainingHeight -= pageHeight;
        yOffset += pageHeight;

        if (remainingHeight > 0) {
          pdf.addPage();
        }
      }

      pdf.save(`anomaly-dashboard-${moment().format("YYYY-MM-DD")}.pdf`);
    } catch (err) {
      console.error("PDF export failed:", err);
      if (caption) caption.style.display = "none";
    } finally {
      setIsExporting(false);
    }
    }, 100);
  };

  const applyFilters = (overrides = {}) => {
    const nextStatus = overrides.status !== undefined ? overrides.status : status;
    const nextFrom = overrides.periodFrom !== undefined ? overrides.periodFrom : periodFrom;
    const nextTo = overrides.periodTo !== undefined ? overrides.periodTo : periodTo;
    setAppliedFilters({ status: nextStatus, periodFrom: nextFrom, periodTo: nextTo });
    fetchStats(buildFilters(apiRegulationId, nextStatus, nextFrom, nextTo));
    closeBranchDrilldown();
  };

  const clearAllFilters = () => {
    const defaults = {
      status: "",
      periodFrom: getAnomalyDefaultPeriodFrom(),
      periodTo: getAnomalyDefaultPeriodTo(),
    };
    setStatus(defaults.status);
    setPeriodFrom(defaults.periodFrom);
    setPeriodTo(defaults.periodTo);
    setRegulationId("");
    applyFilters(defaults);
  };

  const handleGoToAnomalyList = (extra = {}) => {
    navigate("../list", { state: getDashboardListState(extra) });
  };

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!lastCompletedAt) return;
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastCompletedAt]);

  useEffect(() => {
    sessionStorage.setItem(DASH_STORAGE_KEY, JSON.stringify({
      status,
      periodFrom: periodFrom ? periodFrom.toISOString() : null,
      periodTo: periodTo ? periodTo.toISOString() : null,
    }));
  }, [status, periodFrom, periodTo]);

  const getDashboardListState = (extra = {}) => ({
    fromDashboard: true,
    regulationId: apiRegulationId || undefined,
    status: status || undefined,
    periodFrom: periodFrom ? moment(periodFrom).format("YYYY-MM-DD") : undefined,
    periodTo: periodTo ? moment(periodTo).format("YYYY-MM-DD") : undefined,
    ...extra,
  });

  const getDashboardTimeSeriesState = (extra = {}) =>
    buildDashboardTimeSeriesNavState({ periodFrom, periodTo, status }, extra);

  const resolveRegulationId = (key) => {
    const match = glbRegulations?.find(
      (r) => r.name === key || String(r.id) === String(key)
    );
    return match?.id || key;
  };

  const activeRegulationLabel = useMemo(() => {
    if (!regulationId) return null;
    const match = glbRegulations?.find((r) => String(r.id) === String(regulationId));
    if (match?.name) return match.name;
    const statKey = regulationKeys.find((k) => String(resolveRegulationId(k)) === String(regulationId));
    return statKey || regulationId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regulationId, glbRegulations, regulationKeys]);

  // The regulation highlight is visual only (stats are always fetched for the
  // active regulation), so clearing it needs no refetch.
  const clearRegulationFilter = () => setRegulationId("");

  const getRegulationDoughnutData = () => {
    if (!stats?.byRegulation) return { labels: [], datasets: [] };
    const labels = Object.keys(stats.byRegulation);
    const data = labels.map((k) => stats.byRegulation[k]);
    const palette = ["#3b82f6", "#8b5cf6", "#06b6d4", "#f59e0b", "#ef4444", "#10b981"];
    const hasRegFilter = Boolean(regulationId);
    return {
      labels,
      datasets: [{
        data,
        backgroundColor: labels.map((key, i) => {
          if (!hasRegFilter) return palette[i % palette.length];
          return String(resolveRegulationId(key)) === String(regulationId)
            ? palette[i % palette.length]
            : "rgba(148, 163, 184, 0.35)";
        }),
        borderWidth: labels.map((key) => (
          hasRegFilter && String(resolveRegulationId(key)) === String(regulationId) ? 3 : 2
        )),
        borderColor: labels.map((key, i) => (
          hasRegFilter && String(resolveRegulationId(key)) === String(regulationId)
            ? "#1d4ed8"
            : "#fff"
        )),
      }],
    };
  };

  const getBarData = () => {
    if (!stats?.byRule) return { labels: [], datasets: [] };
    const sorted = Object.entries(stats.byRule).sort((a, b) => b[1] - a[1]);
    const labels = sorted.map(([k]) => resolveAnomalyRuleLabel(t, k));
    const data = sorted.map(([, v]) => v);
    const rankDenom = Math.max(sorted.length - 1, 1);
    return {
      labels,
      datasets: [{
        label: t("AnomalyDetection.anomalyCount.label"),
        data,
        backgroundColor: sorted.map(([,], index) => {
          const rankAlpha = 0.4 + (0.55 * (1 - index / rankDenom));
          return `rgba(59, 130, 246, ${rankAlpha.toFixed(2)})`;
        }),
        borderColor: sorted.map((_, index) => (index === 0 ? "#1d4ed8" : "#3b82f6")),
        borderWidth: sorted.map((_, index) => (index === 0 ? 2 : 1)),
        borderRadius: 6,
      }],
    };
  };

  const barRuleCodes = useMemo(() => {
    if (!stats?.byRule) return [];
    return Object.entries(stats.byRule)
      .sort((a, b) => b[1] - a[1])
      .map(([k]) => k);
  }, [stats]);

  const getBranchBarData = () => {
    const drillColor = branchDrillParent ? "rgba(139, 92, 246, 0.75)" : "rgba(59, 130, 246, 0.75)";
    return {
      labels: chartBranches.map((b) => b.branchName),
      datasets: [{
        label: t("AnomalyDetection.anomalyCount.label"),
        data: chartBranches.map((b) => b.count),
        backgroundColor: drillColor,
        borderColor: "rgba(59, 130, 246, 0.35)",
        borderWidth: 1,
        borderRadius: 4,
      }],
    };
  };

  const getLineData = () => {
    if (!stats?.byPeriod) return { labels: [], datasets: [] };
    const labels = trendPeriodLabels.map(formatAnomalyDisplayDate);
    const data = trendPeriodLabels.map((k) => stats.byPeriod[k]);
    return {
      labels,
      datasets: [{
        label: t("AnomalyDetection.anomalyCount.label"),
        data,
        borderColor: "#3b82f6",
        backgroundColor: (context) => {
          const { ctx, chartArea } = context.chart;
          if (!chartArea) return "rgba(59, 130, 246, 0.12)";
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, "rgba(59, 130, 246, 0.28)");
          gradient.addColorStop(1, "rgba(59, 130, 246, 0.02)");
          return gradient;
        },
        pointBackgroundColor: trendPeriodLabels.map((period) =>
          (trendPeak && period === trendPeak.period) ? ANOMALY_SEVERITY_COLORS.CRITICAL : "#3b82f6"
        ),
        pointBorderColor: trendPeriodLabels.map((period) =>
          (trendPeak && period === trendPeak.period) ? ANOMALY_SEVERITY_COLORS.CRITICAL : "#ffffff"
        ),
        pointBorderWidth: trendPeriodLabels.map((period) =>
          (trendPeak && period === trendPeak.period) ? 2 : 1
        ),
        pointRadius: trendPeriodLabels.map((period) =>
          (trendPeak && period === trendPeak.period) ? 7 : 4
        ),
        tension: 0.4,
        fill: true,
      }],
    };
  };

  const getHeatmapPeriods = () => {
    if (!stats?.heatmap?.length) return [];
    return [...new Set(stats.heatmap.map((h) => h.period))].sort((a, b) => a.localeCompare(b));
  };

  const getHeatmapMetrics = () => {
    if (!stats?.heatmap?.length) return [];
    const map = {};
    stats.heatmap.forEach((h) => {
      if (!map[h.metric]) map[h.metric] = { metricId: h.metricId, metric: h.metric, total: 0 };
      map[h.metric].total += h.count;
    });
    return Object.values(map).sort((a, b) => b.total - a.total).slice(0, 20);
  };

  const getHeatmapCell = (metricName, period) => {
    if (!stats?.heatmap) return { count: 0, metricId: null };
    const entry = stats.heatmap.find((h) => h.metric === metricName && h.period === period);
    return entry ? { count: entry.count, metricId: entry.metricId } : { count: 0, metricId: null };
  };

  const getPeakPeriodForMetric = (metricName, periods) => {
    let peak = { period: periods[0] || "", count: 0 };
    periods.forEach((period) => {
      const { count } = getHeatmapCell(metricName, period);
      if (count > peak.count) peak = { period, count };
    });
    return peak;
  };

  const heatmapPeriods = getHeatmapPeriods();
  const heatmapMetrics = getHeatmapMetrics();

  const regulationDoughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "68%",
    plugins: {
      legend: {
        position: "bottom",
        labels: { font: { family: "Inter, sans-serif", size: 11 }, usePointStyle: true },
      },
    },
    onHover: chartCursorHover,
    onClick: (_, elements) => {
      if (!elements.length || !regulationKeys.length) return;
      const key = regulationKeys[elements[0].index];
      if (key) setRegulationId(resolveRegulationId(key));
    },
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => t("AnomalyDetection.ruleBarTooltip.label", { count: ctx.parsed.y }),
        },
      },
    },
    onHover: chartCursorHover,
    scales: {
      x: {
        ticks: {
          font: { size: 11 },
          maxRotation: 45,
          minRotation: 0,
        },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: { font: { size: 11 }, precision: 0 },
        grid: { color: "rgba(226,232,240,0.6)" },
      },
    },
    onClick: (_, elements) => {
      if (!elements.length) return;
      const code = barRuleCodes[elements[0].index];
      if (code) navigate("../list", { state: getDashboardListState({ ruleCode: code }) });
    },
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => t("AnomalyDetection.trendPointTooltip.label", { count: ctx.parsed.y }),
        },
      },
    },
    onHover: chartCursorHover,
    scales: {
      x: { ticks: { font: { size: 11 } } },
      y: { ticks: { font: { size: 11 } }, beginAtZero: true },
    },
    onClick: (_, elements) => {
      if (!elements.length || !stats?.byPeriod) return;
      const period = trendPeriodLabels[elements[0].index];
      if (period) {
        const periodMoment = moment(
          period,
          [moment.ISO_8601, "YYYY-MM-DD", "YYYY-MM", "YYYY-[Q]Q Q", "YYYY-[Q]Q"],
          true
        );
        const extra = periodMoment.isValid()
          ? {
            periodFrom: periodMoment.clone().startOf("month").format("YYYY-MM-DD"),
            periodTo: periodMoment.clone().endOf("month").format("YYYY-MM-DD"),
          }
          : {};
        navigate("../list", { state: getDashboardListState(extra) });
      }
    },
  };

  const branchBarOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => (
            branchDrillParent
              ? t("AnomalyDetection.branchBarTooltipList.label", { count: ctx.parsed.x })
              : t("AnomalyDetection.branchBarTooltipDrill.label", { count: ctx.parsed.x })
          ),
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: { font: { size: 11 }, precision: 0 },
        grid: { color: "rgba(226,232,240,0.6)" },
      },
      y: { ticks: { font: { size: 12, weight: "500" } }, grid: { display: false } },
    },
    onHover: chartCursorHover,
    onClick: (_, elements) => {
      if (!elements.length) return;
      const branch = chartBranches[elements[0].index];
      if (!branch) return;
      if (branchDrillParent) {
        navigate("../list", { state: getDashboardListState({ branchId: branch.branchId, branchName: branch.branchName }) });
      } else {
        openBranchDrilldown(branch.branchId, branch.branchName);
      }
    },
  };

  const renderSeveritySpectrum = () => {
    const segments = ANOMALY_SEVERITY_ORDER.map((key) => ({
      key,
      count: stats?.bySeverity?.[key] ?? 0,
      color: ANOMALY_SEVERITY_COLORS[key],
      label: t(`AnomalyDetection.${key.toLowerCase()}.label`),
      pct: severityTotal > 0
        ? Math.round(((stats?.bySeverity?.[key] ?? 0) / severityTotal) * 100)
        : 0,
    }));

    return (
      <div
        className="severity-spectrum-card severity-spectrum-card--glass"
        data-testid="severity-spectrum"
      >
        <div className="spectrum-glass-shine" aria-hidden="true" />
        <div className="spectrum-card-header">
          <h6 className="chart-header-with-tip">
            <span className="chart-header-icon chart-header-icon--blue" aria-hidden="true">
              <FiActivity />
            </span>
            {t("AnomalyDetection.severitySpectrum.label")}
            <AnomalyInfoTip text={t("AnomalyDetection.severitySpectrumToolTip.label")} />
          </h6>
          <span className="spectrum-total-meta">
            {t("AnomalyDetection.inSelectedPeriod.label")}
          </span>
        </div>
        <div className="spectrum-card-body">
              <div className="spectrum-summary-row">
                <div className="spectrum-hero-stat">
                  <span className="spectrum-hero-value">{formatSpectrumCount(severityTotal)}</span>
                  <span className="spectrum-hero-label">{t("AnomalyDetection.allSeverities.label")}</span>
                  <span className="spectrum-hero-caption">
                    {t("AnomalyDetection.inSelectedPeriod.label")}
                  </span>
                </div>
                <div className="spectrum-bar-panel">
                  <div className="severity-spectrum-bar-track">
                    <div
                      className="severity-spectrum-bar"
                      role="group"
                      aria-label={t("AnomalyDetection.severitySpectrum.label")}
                    >
                      {segments.map((seg) => {
                        if (!seg.count) return null;
                        return (
                          <button
                            key={seg.key}
                            type="button"
                            className={`spectrum-segment spectrum-segment--${seg.key.toLowerCase()}`}
                            style={{
                              flexGrow: seg.count,
                              flexBasis: 0,
                              minWidth: "24px",
                              "--seg-color": seg.color,
                            }}
                            title={`${seg.label}: ${seg.count} (${seg.pct}%)`}
                            onClick={() =>
                              navigate("../list", { state: getDashboardListState({ severity: seg.key }) })
                            }
                          >
                            <span className="spectrum-segment-fill" />
                            {seg.pct >= 8 && (
                              <span className="spectrum-segment-pct">{seg.pct}%</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    <div className="spectrum-bar-gloss" aria-hidden="true" />
                  </div>
                  {severityTotal === 0 && (
                    <p className="spectrum-bar-empty">{t("Nodatafound.label")}</p>
                  )}
                </div>
              </div>
              <div className="spectrum-tier-grid">
                {segments.map((seg) => (
                  <button
                    key={seg.key}
                    type="button"
                    className={`spectrum-tier spectrum-tier--${seg.key.toLowerCase()}${!seg.count ? " spectrum-tier--empty" : ""}`}
                    style={{ "--tier-color": seg.color }}
                    disabled={!seg.count}
                    onClick={() =>
                      navigate("../list", { state: getDashboardListState({ severity: seg.key }) })
                    }
                  >
                    <div className="spectrum-tier-label-dot">
                      <span className="spectrum-tier-dot" aria-hidden="true" />
                      <span className="spectrum-tier-label">{seg.label}</span>
                    </div>
                    <div className="spectrum-tier-count-pct">
                      <span className="spectrum-tier-count">{formatSpectrumCount(seg.count)}</span>
                      <span className="spectrum-tier-pct">
                        {seg.pct}% {t("AnomalyDetection.ofTotal.label")}
                      </span>
                    </div>

                  </button>
                ))}
              </div>
        </div>
      </div>
    );
  };

  const renderHeatmap = () => (
    <div className="anomaly-chart-card heatmap-card" data-testid="anomaly-heatmap">
      <div className="chart-card-header heatmap-card-header">
        <div className="heatmap-card-header-main">
          <h6 className="chart-header-with-tip">
            <span className="chart-header-icon chart-header-icon--red" aria-hidden="true">
              <FiGrid />
            </span>
            {t("AnomalyDetection.heatmap.label")}
            <AnomalyInfoTip text={t("AnomalyDetection.heatmapToolTip.label")} />
          </h6>
          <small className="chart-note-text">{t("AnomalyDetection.heatmapNote.label")}</small>
        </div>
        {heatmapHotspot && (
          <div className="heatmap-hotspot-inline">
            <span className="heatmap-hotspot-badge">{t("AnomalyDetection.heatmapHotspotBadge.label")}</span>
            <span className="heatmap-hotspot-text">
              {t("AnomalyDetection.hotspotCallout.label", {
                metric: heatmapHotspot.metric,
                count: heatmapHotspot.count,
                period: formatAnomalyDisplayDate(heatmapHotspot.period),
              })}
            </span>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() =>
                navigate("../time_series", {
                  state: getDashboardTimeSeriesState({
                    metricId: heatmapHotspot.metricId,
                    metricName: heatmapHotspot.metric,
                    selectedPeriod: heatmapHotspot.period,
                  }),
                })
              }
            >
              {t("AnomalyDetection.investigate.label")}
            </Button>
          </div>
        )}
      </div>

      <div className="chart-card-body p-0">
        {heatmapMetrics.length > 0 ? (
          <>
            <div className="heatmap-legend" aria-hidden="true">
              <span className="heatmap-legend-label">{t("AnomalyDetection.heatmapLegend.label")}</span>
              <div className="heatmap-legend-scale">
                <span className="heatmap-legend-step heatmap-legend-step--0" />
                <span className="heatmap-legend-step heatmap-legend-step--25" />
                <span className="heatmap-legend-step heatmap-legend-step--50" />
                <span className="heatmap-legend-step heatmap-legend-step--75" />
                <span className="heatmap-legend-step heatmap-legend-step--100" />
              </div>
              <span className="heatmap-legend-end">{t("AnomalyDetection.heatmapLegendHigh.label")}</span>
              <span className="heatmap-legend-max">
                {t("AnomalyDetection.heatmapMaxInView.label", { max: heatmapMaxCount })}
              </span>
            </div>
            <div className="heatmap-wrapper">
              <div className="heatmap-grid">
                <div className="heatmap-grid-row heatmap-grid-row--header">
                  <div className="heatmap-grid-metric-col heatmap-grid-metric-col--header">
                    {t("AnomalyDetection.metric.label")}
                  </div>
                  <div className="heatmap-grid-periods">
                    {heatmapPeriods.map((p) => (
                      <div key={p} className="heatmap-period-label" title={formatAnomalyDisplayDate(p)}>
                        {formatAnomalyHeatmapPeriod(p)}
                      </div>
                    ))}
                  </div>
                  <div className="heatmap-grid-total-col heatmap-grid-total-col--header">
                    {t("AnomalyDetection.heatmapRowTotal.label")}
                  </div>
                </div>
                {heatmapMetrics.map((row) => {
                  const peak = getPeakPeriodForMetric(row.metric, heatmapPeriods);
                  return (
                    <div key={row.metric} className="heatmap-grid-row">
                      <div className="heatmap-grid-metric-col">
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <BootstrapTooltip>{t("AnomalyDetection.heatmapMetricTooltip.label")}</BootstrapTooltip>
                          }
                        >
                          <button
                            type="button"
                            className="heatmap-metric-btn"
                            onClick={() =>
                              navigate("../time_series", {
                                state: getDashboardTimeSeriesState({
                                  metricId: row.metricId,
                                  metricName: row.metric,
                                }),
                              })
                            }
                          >
                            <span className="heatmap-metric-name">{row.metric}</span>
                          </button>
                        </OverlayTrigger>
                      </div>
                      <div className="heatmap-grid-periods">
                        {heatmapPeriods.map((period) => {
                          const { count, metricId } = getHeatmapCell(row.metric, period);
                          const intensity = getHeatmapIntensity(count, heatmapMaxCount);
                          const isHotspot = heatmapHotspot
                            && heatmapHotspot.metric === row.metric
                            && heatmapHotspot.period === period;
                          const isRowPeak = !isHotspot && count > 0 && period === peak.period;
                          const cellClasses = [
                            "heatmap-grid-cell",
                            count ? "heatmap-grid-cell--filled" : "heatmap-grid-cell--empty",
                            count ? "heatmap-grid-cell--clickable" : "",
                            isHotspot ? "heatmap-grid-cell--hotspot" : "",
                            isRowPeak ? "heatmap-grid-cell--row-peak" : "",
                          ].filter(Boolean).join(" ");

                          const cellButton = (
                            <button
                              type="button"
                              className={cellClasses}
                              style={{
                                "--hm-intensity": intensity,
                                color: getHeatmapCellTextColor(intensity),
                              }}
                              disabled={!count}
                              aria-label={
                                count
                                  ? t("AnomalyDetection.heatmapCellTooltip.label", {
                                    count,
                                    metric: row.metric,
                                    period,
                                  })
                                  : t("AnomalyDetection.heatmapCellEmpty.label", {
                                    metric: row.metric,
                                    period,
                                  })
                              }
                              onClick={() => {
                                if (count && metricId) {
                                  navigate("../time_series", {
                                    state: getDashboardTimeSeriesState({
                                      metricId,
                                      metricName: row.metric,
                                      selectedPeriod: period,
                                    }),
                                  });
                                }
                              }}
                            >
                              {count > 0 ? count : ""}
                            </button>
                          );

                          if (!count) return <div key={period} className="heatmap-cell-slot">{cellButton}</div>;

                          return (
                            <div key={period} className="heatmap-cell-slot">
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <BootstrapTooltip>
                                    {t("AnomalyDetection.heatmapCellTooltip.label", {
                                      count,
                                      metric: row.metric,
                                      period,
                                    })}
                                  </BootstrapTooltip>
                                }
                              >
                                {cellButton}
                              </OverlayTrigger>
                            </div>
                          );
                        })}
                      </div>
                      <div className="heatmap-grid-total-col" aria-label={t("AnomalyDetection.heatmapRowTotal.label")}>
                        {row.total}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <div className="anomaly-empty-state">
            <FcFinePrint />
            <span>{t("Nodatafound.label")}</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderChartsContent = () => (
    <>
      {/* Dismissal warning banner */}
      {dismissal.rate > 0.4 && dismissal.total > 0 && (
        <div className="dismissal-warning-banner">
          <div className="dismissal-warning-text">
            <span className="dismissal-warning-icon">⚠</span>
            {t("AnomalyDetection.dismissalWarning.label")}
          </div>
          <Button
            variant="outline-dark"
            size="sm"
            onClick={() => navigate("../rule_configs")}
          >
            {t("AnomalyDetection.configureRules.label")}
          </Button>
        </div>
      )}

      {/* Overview band: resolution rate beside the severity spectrum */}
      <div className="dashboard-overview-grid">
        <div className="overview-side-col">
          <div className="resolution-card">
            <div className="resolution-header">
              <span className="resolution-label label-with-tip">
                <span className="chart-header-icon chart-header-icon--green" aria-hidden="true">
                  <FiCheckCircle />
                </span>
                {t("AnomalyDetection.resolutionRate.label")}
                <AnomalyInfoTip text={t("AnomalyDetection.resolutionRateToolTip.label")} />
              </span>
              <span className="resolution-pct">{resolution.rate}%</span>
            </div>
            <div className="resolution-bar-track">
              <div
                className="resolution-bar-fill"
                style={{ width: `${resolution.rate}%`, backgroundColor: resolution.barColor }}
              />
            </div>
            <p className="resolution-caption">
              {t("AnomalyDetection.resolutionCaption.label", {
                resolved: resolution.resolved,
                total: resolution.total,
                rate: resolution.rate,
              })}
            </p>
          </div>
        </div>

        {renderSeveritySpectrum()}
      </div>

      {/* Regulation donut + Branch bar + Rule bar — side by side */}
      <div className="row g-3 mb-3">
        {regulationKeys.length > 1 && (
          <div className="col-md-4">
            <div className="anomaly-chart-card">
              <div className="chart-card-header">
                <h6 className="chart-header-with-tip">
                  <span className="chart-header-icon chart-header-icon--violet" aria-hidden="true">
                    <FiPieChart />
                  </span>
                  {t("AnomalyDetection.byRegulation.label")}
                  <AnomalyInfoTip text={t("AnomalyDetection.byRegulationToolTip.label")} />
                </h6>
                <small className="chart-note-text">{t("AnomalyDetection.clickToFilter.label")}</small>
              </div>
              <div className="chart-card-body">
                <div className="chart-height-md">
                  <Doughnut data={getRegulationDoughnutData()} options={regulationDoughnutOptions} />
                </div>
              </div>
            </div>
          </div>
        )}
        <div className={regulationKeys.length > 1 ? "col-md-4" : "col-md-6"}>
          {/* Branch chart with inline drill-down */}
          <div className="anomaly-chart-card">
            <div className="chart-card-header heatmap-card-header">
              <div className="heatmap-card-header-main">
                <h6 className="chart-header-with-tip">
                  <span className="chart-header-icon chart-header-icon--blue" aria-hidden="true">
                    <FiGitBranch />
                  </span>
                  {t("AnomalyDetection.byBranch.label")}
                  <AnomalyInfoTip text={t("AnomalyDetection.byBranchToolTip.label")} />
                </h6>
                {branchDrillParent && (
                  <div className="branch-breadcrumb">
                    <button type="button" className="breadcrumb-link" onClick={closeBranchDrilldown}>
                      {t("AnomalyDetection.allBranches.label")}
                    </button>
                    <span className="breadcrumb-sep">&gt;</span>
                    <span className="breadcrumb-current">{branchDrillParent.branchName}</span>
                  </div>
                )}
                <small className="chart-note-text">
                  {branchDrillParent
                    ? t("AnomalyDetection.branchBarNoteList.label")
                    : t("AnomalyDetection.branchBarNoteDrill.label")}
                  {hasMoreBranches && (
                    <> · {t("AnomalyDetection.branchChartTopN.label", { limit: BRANCH_CHART_LIMIT })}</>
                  )}
                </small>
              </div>
              {topBranch && !branchDrillParent && (
                <div className="heatmap-hotspot-inline heatmap-hotspot-inline--branch">
                  <span className="heatmap-hotspot-badge">{t("AnomalyDetection.branchTopBadge.label")}</span>
                  <span className="heatmap-hotspot-text">
                    {t("AnomalyDetection.branchTopCallout.label", {
                      branch: topBranch.branchName,
                      count: topBranch.count,
                    })}
                  </span>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() =>
                      navigate("../list", { state: getDashboardListState({ branchId: topBranch.branchId, branchName: topBranch.branchName }) })
                    }
                  >
                    {t("AnomalyDetection.investigate.label")}
                  </Button>
                </div>
              )}
            </div>
            <div className="chart-card-body">
              {sortedBranches.length > 0 ? (
                <>
                  <div
                    className={`chart-bar-scroll chart-bar-scroll--branches${loadingBranchDrill ? " chart-bar-scroll--loading" : ""}`}
                    style={{ height: `${Math.max(180, chartBranches.length * 38)}px` }}
                  >
                    {loadingBranchDrill && (
                      <div className="chart-loading-overlay" data-testid="branch-drill-loading">
                        <ScaleLoader color="#3b82f6" height={24} />
                      </div>
                    )}
                    <Bar data={getBranchBarData()} options={branchBarOptions} />
                  </div>
                  {hasMoreBranches && (
                    <div className="chart-view-all-row">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handleGoToAnomalyList()}
                      >
                        {t("AnomalyDetection.branchViewAll.label", { count: sortedBranches.length })}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="anomaly-empty-state">
                  <FcFinePrint />
                  <span>{t("Nodatafound.label")}</span>
                </div>
              )}
            </div>
          </div>

        </div>
        <div className={regulationKeys.length > 1 ? "col-md-4" : "col-md-6"}>
          <div className="anomaly-chart-card">
            <div className="chart-card-header heatmap-card-header">
              <div className="heatmap-card-header-main">
                <h6 className="chart-header-with-tip">
                  <span className="chart-header-icon chart-header-icon--amber" aria-hidden="true">
                    <FiBarChart2 />
                  </span>
                  {t("AnomalyDetection.byRule.label")}
                  <AnomalyInfoTip text={t("AnomalyDetection.byRuleToolTip.label")} />
                </h6>
                <small className="chart-note-text">{t("AnomalyDetection.clickToViewInList.label")}</small>
              </div>
              {topRule && (
                <div className="heatmap-hotspot-inline heatmap-hotspot-inline--rule">
                  <span className="heatmap-hotspot-badge">{t("AnomalyDetection.topRuleSpotlight.label")}</span>
                  <span className="heatmap-hotspot-text">
                    {t("AnomalyDetection.ruleTopCallout.label", {
                      rule: resolveAnomalyRuleLabel(t, topRule.code),
                      count: topRule.count,
                    })}
                  </span>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => navigate("../list", { state: getDashboardListState({ ruleCode: topRule.code }) })}
                  >
                    {t("AnomalyDetection.investigate.label")}
                  </Button>
                </div>
              )}
            </div>
            <div className="chart-card-body">
              {stats?.byRule && Object.keys(stats.byRule).length > 0 ? (
                <div className="chart-height-md">
                  <Bar data={getBarData()} options={barOptions} />
                </div>
              ) : (
                <div className="anomaly-empty-state">
                  <FcFinePrint />
                  <span>{t("Nodatafound.label")}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Trend line */}
      <div className="anomaly-chart-card mb-3">
        <div className="chart-card-header heatmap-card-header">
          <div className="heatmap-card-header-main">
            <h6 className="chart-header-with-tip">
              <span className="chart-header-icon chart-header-icon--blue" aria-hidden="true">
                <FiTrendingUp />
              </span>
              {t("AnomalyDetection.trendOverTime.label")}
              <AnomalyInfoTip text={t("AnomalyDetection.trendOverTimeToolTip.label")} />
            </h6>
            <small className="chart-note-text">{t("AnomalyDetection.trendClickNote.label")}</small>
          </div>
          {trendPeak && (
            <div className="heatmap-hotspot-inline heatmap-hotspot-inline--trend">
              <span className="heatmap-hotspot-badge">{t("AnomalyDetection.trendPeakBadge.label")}</span>
              <span className="heatmap-hotspot-text">
                {t("AnomalyDetection.trendPeakCallout.label", {
                  period: formatAnomalyHeatmapPeriod(trendPeak.period),
                  count: trendPeak.count,
                })}
              </span>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  const periodMoment = moment(
                    trendPeak.period,
                    [moment.ISO_8601, "YYYY-MM-DD", "YYYY-MM", "YYYY-[Q]Q Q", "YYYY-[Q]Q"],
                    true
                  );
                  const extra = periodMoment.isValid()
                    ? {
                      periodFrom: periodMoment.clone().startOf("month").format("YYYY-MM-DD"),
                      periodTo: periodMoment.clone().endOf("month").format("YYYY-MM-DD"),
                    }
                    : {};
                  navigate("../list", { state: getDashboardListState(extra) });
                }}
              >
                {t("AnomalyDetection.investigate.label")}
              </Button>
            </div>
          )}
        </div>
        <div className="chart-card-body">
          {stats?.byPeriod && Object.keys(stats.byPeriod).length > 0 ? (
            <div className="chart-height-sm">
              <Line data={getLineData()} options={lineOptions} />
            </div>
          ) : (
            <div className="anomaly-empty-state">
              <FcFinePrint />
              <span>{t("Nodatafound.label")}</span>
            </div>
          )}
        </div>
      </div>

      {renderHeatmap()}
    </>
  );

  return (
    <div className="anomaly-dashboard">
      <div className="dashboard-page-header">
        <div className="dashboard-page-title">
          <h5 className="MH">{t("AnomalyDashboard.label")}</h5>
          <p className="dashboard-page-subtitle">{t("AnomalyDashboard.subtitle.label")}</p>
        </div>
        {(lastRun || lastCompletedAt) && (
          <span className="dashboard-freshness-note">
            {lastRun
              ? t("AnomalyDetection.lastDetectionRun.label", {
                  time: formatAnomalyDisplayDateTime(lastRun.completedAt),
                })
              : t("AnomalyDetection.lastDetectionRun.label", {
                  time: formatAnomalyDisplayDateTime(lastCompletedAt),
                })}
            {lastRun?.monthLabel && (
              <span className="dashboard-freshness-meta">
                {" · "}{lastRun.monthLabel}
              </span>
            )}
            {lastRun?.totalRules != null && (
              <span className="dashboard-freshness-meta">
                {" · "}{t("AnomalyDetection.detectProgress.label", {
                  processed: lastRun.processedRules,
                  total: lastRun.totalRules,
                })}
              </span>
            )}
          </span>
        )}
      </div>

      <div className="dashboard-command-strip">
        <div className="command-strip-filters">
          <div className="filter-group">
            <label className="label-with-tip">
              {t("AnomalyDetection.period.label")}
              <AnomalyInfoTip text={t("AnomalyDetection.periodFilterToolTip.label")} />
            </label>
            <AnomalyPeriodSelector
              onChange={(from, to) => {
                setPeriodFrom(from);
                setPeriodTo(to);
              }}
            />
          </div>

          <div className="filter-group">
            <label className="label-with-tip">
              {t("AnomalyDetection.status.label")}
              <AnomalyInfoTip text={t("AnomalyDetection.statusFilterToolTip.label")} />
            </label>
            <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">{t("AnomalyDetection.statusAll.label")}</option>
              <option value="OPEN">{formatAnomalyStatusLabel("OPEN")}</option>
              <option value="DISMISSED">{formatAnomalyStatusLabel("DISMISSED")}</option>
              <option value="RESOLVED">{formatAnomalyStatusLabel("RESOLVED")}</option>
            </Form.Select>
          </div>

          {filtersDirty ? (
            <OverlayTrigger
              placement="top"
              overlay={Utils.renderTooltip(t("AnomalyDetection.filtersDirtyHint.label"))}
            >
              <Button
                variant="primary"
                size="sm"
                className="apply-filters-btn apply-filters-btn--dirty"
                onClick={() => applyFilters()}
              >
                {t("AnomalyDetection.applyFilters.label")}
              </Button>
            </OverlayTrigger>
          ) : (
            <Button
              variant="primary"
              size="sm"
              className="apply-filters-btn"
              onClick={() => applyFilters()}
            >
              {t("AnomalyDetection.applyFilters.label")}
            </Button>
          )}

          {regulationId && (
            <span className="filter-active-chip filter-active-chip--regulation">
              {t("AnomalyDetection.regulationFilterActive.label", { name: activeRegulationLabel })}
              <button
                type="button"
                className="filter-active-chip-clear"
                aria-label={t("AnomalyDetection.clearRegulationFilter.label")}
                onClick={clearRegulationFilter}
              >
                ×
              </button>
            </span>
          )}
        </div>

        <div className="command-strip-actions">
          <OverlayTrigger
            placement="top"
            overlay={Utils.renderTooltip(
              isExporting
                ? t("AnomalyDetection.exporting.label")
                : t("AnomalyDetection.exportPdfToolTip.label")
            )}
          >
            <button
              className={`pdf-icon-btn${isExporting ? " pdf-icon-btn--loading" : ""}`}
              onClick={handleExportPdf}
              disabled={isExporting}
              aria-label={t("AnomalyDetection.exportPdf.label")}
            >
              {isExporting
                ? <ScaleLoader color="#fff" height={12} width={2} margin={1} />
                : <BsFiletypePdf size={20} />}
            </button>
          </OverlayTrigger>
          <Button
            variant="outline-secondary"
            size="sm"
            className="rule-config-shortcut-btn"
            onClick={() => navigate("../rule_configs")}
          >
            {t("AnomalyRuleConfig.label")}
          </Button>
          <OverlayTrigger
            placement="top"
            overlay={Utils.renderTooltip(t("AnomalyDashboard.goToAnomalyListToolTip.label"))}
          >
            <Button
              variant="primary"
              size="sm"
              onClick={() => handleGoToAnomalyList()}
            >
              {t("AnomalyList.label")}
            </Button>
          </OverlayTrigger>
        </div>
      </div>

      <div ref={contentRef}>
        <div className="pdf-export-caption">
          {t("AnomalyDetection.pdfGeneratedOn.label", {
            date: formatAnomalyDisplayDateTime(new Date()),
          })}
        </div>
        {loadingStats && !stats ? (
          <div className="anomaly-chart-card">
            <div className="chart-card-body">
              <div className="anomaly-loading-center">
                <ScaleLoader color="#3b82f6" height={36} />
              </div>
            </div>
          </div>
        ) : (
          <div className="dashboard-content-shell">
            {loadingStats && (
              <div className="dashboard-refresh-overlay" data-testid="dashboard-refresh-overlay">
                <div className="refresh-overlay-spinner">
                  <ScaleLoader color="#3b82f6" height={30} />
                </div>
              </div>
            )}
            {statsEmpty ? (
              <div className="dashboard-empty-state">
                {hasActiveFilters ? (
                  <>
                    <FcFinePrint className="empty-state-icon" />
                    <h5>{t("AnomalyDetection.noAnomaliesInRange.label")}</h5>
                    <p>{t("AnomalyDetection.widenDateRange.label")}</p>
                    <div className="empty-state-actions">
                      <Button variant="outline-secondary" onClick={clearAllFilters}>
                        {t("AnomalyDetection.clearFilters.label")}
                      </Button>
                      <OverlayTrigger
                        placement="top"
                        overlay={Utils.renderTooltip(t("AnomalyDashboard.goToAnomalyListToolTip.label"))}
                      >
                        <Button
                          variant="primary"
                          onClick={() => handleGoToAnomalyList()}
                        >
                          {t("AnomalyList.label")}
                        </Button>
                      </OverlayTrigger>
                    </div>
                  </>
                ) : (
                  <>
                    <FiCheckCircle className="empty-state-icon" />
                    <h5>{t("AnomalyDetection.noAnomaliesDetected.label")}</h5>
                    <p>{t("AnomalyDetection.noAnomaliesSubtext.label")}</p>
                    <OverlayTrigger
                      placement="top"
                      overlay={Utils.renderTooltip(t("AnomalyDashboard.goToAnomalyListToolTip.label"))}
                    >
                      <Button
                        variant="primary"
                        onClick={() => handleGoToAnomalyList()}
                      >
                        {t("AnomalyList.label")}
                      </Button>
                    </OverlayTrigger>
                  </>
                )}
              </div>
            ) : (
              renderChartsContent()
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default withTranslation()(AnomalyDashboard);
